import { Stack, Step, StepLabel, Stepper, styled } from '@mui/material';
import React, { FC } from 'react';
import { OrderType } from 'src/types/order';
import { StepIconProps } from '@mui/material/StepIcon';
import StepConnector, { stepConnectorClasses } from '@mui/material/StepConnector';
import translate from '@translate';

import Typography from 'src/components/Typography';
import { DateType } from 'src/types/date';
import moment from 'moment';
import { colors } from 'src/themes/default';
import { StackProps } from '@mui/system';
import { useTheme } from '@mui/material/styles';
import useMediaQuery from '@mui/material/useMediaQuery';
import { OrderChangelogType } from 'src/types/orderDetail';
import ReportIcon from '@mui/icons-material/Report';
import Tooltip from 'src/components/Tooltip';
import { getStatusIconComponent, getStatusIconType, isAtPickupPointStepVisible } from './functions';

type IconStepType = 'received' | 'processing' | 'waitingForCarrier' | 'onTheWay' | 'atPickupPoint' | 'delivered';

const WarningBlock = styled('div')({
    position: 'absolute',
    top: '-6px',
    right: 'calc(50% - 27px)',
    zIndex: 1,
    color: colors.alert_OR,
});

const progressSteps: {
    label: string;
    timeKey: string;
    key: IconStepType;
}[] = [
    {
        label: translate('orderDetail.status.received'),
        timeKey: 'SHIPMENT_ORDERED_AT',
        key: 'received',
    },
    {
        label: translate('orderDetail.status.processing'),
        timeKey: 'IN_PROCESS_AT',
        key: 'processing',
    },
    {
        label: translate('orderDetail.status.waitingForCarrier'),
        timeKey: 'WAITING_FOR_CARRIER_AT',
        key: 'waitingForCarrier',
    },
    {
        label: translate('orderDetail.status.onTheWay'),
        timeKey: 'SHIPPED_AT',
        key: 'onTheWay',
    },
    {
        label: translate('orderDetail.status.atPickupPoint'),
        timeKey: 'AT_PICKUP_POINT_AT',
        key: 'atPickupPoint',
    },
    {
        label: translate('orderDetail.delivery'),
        timeKey: 'DELIVERED_AT',
        key: 'delivered',
    },
];

const formatTime = (data: DateType, mobile?: boolean) => {
    if (!data) {
        return ' ';
    }
    if (mobile) {
        return `${moment(data).format('D.M.')} ${moment(data).format('LT')}`;
    }

    return moment(data).format('lll');
};

const StyledStack = styled(Stack)<StackProps>(({ theme }) => ({
    width: '100%',
    margin: '20px auto',
    maxWidth: '700px',
}));

const ColorlibStepIconRoot = styled('div')<{
    ownerState: { completed?: boolean; active?: boolean };
}>(({ theme, ownerState }) => ({
    zIndex: 1,
}));

const ColorlibStepIcon = (props: StepIconProps) => {
    const { active, completed, className } = props;

    const classNameParams = className?.split('-') || [];

    return (
        <ColorlibStepIconRoot ownerState={{ completed, active }} className={className}>
            {getStatusIconComponent(classNameParams[1], classNameParams[2])}
        </ColorlibStepIconRoot>
    );
};

const ColorlibConnector = styled(StepConnector)(({ theme }) => ({
    [`&.${stepConnectorClasses.alternativeLabel}`]: {
        top: 28,

        '&  .MuiStepConnector-lineHorizontal': {
            borderColor: colors.text_T3,
        },
    },
}));

export interface ProgressPropsType {
    data: OrderType | null;
    changelog: OrderChangelogType | null;
}

const Progress: FC<ProgressPropsType> = (props) => {
    const theme = useTheme();
    const mobile = useMediaQuery(theme.breakpoints.down('sm'));

    if (!props.data) {
        return null;
    }

    const icons = getStatusIconType(props.data.STATUS, props.changelog);
    const visibleSteps = isAtPickupPointStepVisible(props.data)
        ? progressSteps
        : progressSteps.filter((step) => step.key !== 'atPickupPoint');

    return (
        <StyledStack spacing={4}>
            <Stepper alternativeLabel activeStep={1} connector={<ColorlibConnector />}>
                {visibleSteps.map((step) => (
                    <Step key={step.label}>
                        <StepLabel
                            StepIconComponent={ColorlibStepIcon}
                            StepIconProps={{ className: `order_progress_step-${step.key}-${icons[step.key]}` }}
                        >
                            <>
                                {step.key === 'processing' && icons.messageProcessing && (
                                    <WarningBlock>
                                        <Tooltip title={icons.messageProcessing}>
                                            <ReportIcon />
                                        </Tooltip>
                                    </WarningBlock>
                                )}
                                {!mobile && <Typography variant="h3">{step.label}</Typography>}
                                <Typography variant="body2" gutterBottom>
                                    {formatTime(props.data && props.data[step.timeKey], mobile)}
                                </Typography>
                            </>
                        </StepLabel>
                    </Step>
                ))}
            </Stepper>
        </StyledStack>
    );
};

export default Progress;
